import React, { useEffect, useState } from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, Radio,
    RadioGroup, TextField, Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types/auth';
import { jobVacancyService, HireOptions } from '../../services/jobVacancyService';

interface Props {
    open: boolean;
    vacancyId: number | null;
    vacancyLabel?: string;
    onClose: () => void;
    onHired: (message: string) => void;
}

const newPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const random = new Uint32Array(10);
    window.crypto.getRandomValues(random);
    return `Q!${Array.from(random, (n) => chars[n % chars.length]).join('')}7a`;
};

/** Fecha a vaga informando quem foi contratado; a pessoa já entra alocada na posição do projeto. */
const HireDialog: React.FC<Props> = ({ open, vacancyId, vacancyLabel, onClose, onHired }) => {
    const { user } = useAuth();
    const canCreateUser = user?.role === UserRole.RH || user?.role === UserRole.DIRETOR;
    const [options, setOptions] = useState<HireOptions | null>(null);
    const [applicationId, setApplicationId] = useState('');
    const [mode, setMode] = useState<'existing' | 'new'>('existing');
    const [userId, setUserId] = useState('');
    const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });
    const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState('');
    const [percentage, setPercentage] = useState('100');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !vacancyId) return;
        setError(null);
        setApplicationId('');
        setUserId('');
        setMode('existing');
        setNewUser({ name: '', email: '', password: newPassword() });
        setStartDate(format(new Date(), 'yyyy-MM-dd'));
        setEndDate('');
        setPercentage('100');
        jobVacancyService.getHireOptions(vacancyId).then(setOptions)
            .catch((err) => setError(err?.response?.data?.error || 'Erro ao carregar as candidaturas.'));
    }, [open, vacancyId]);

    const selectApplication = (id: string) => {
        setApplicationId(id);
        const application = options?.applications.find((a) => String(a.id) === id);
        if (!application) return;
        // Candidato já com usuário (mesmo e-mail)? Sugere o usuário; senão, sugere criar a partir do candidato
        const existing = options?.users.find((u) => u.email.toLowerCase() === application.candidate_email.toLowerCase());
        if (existing) {
            setMode('existing');
            setUserId(String(existing.id));
        } else {
            setMode(canCreateUser ? 'new' : 'existing');
            setNewUser((prev) => ({ ...prev, name: application.candidate_name, email: application.candidate_email }));
        }
    };

    const datesInvalid = !!endDate && endDate < startDate;
    const ready = !!applicationId && !!startDate && !datesInvalid &&
        (mode === 'existing' ? !!userId : !!(newUser.name.trim() && newUser.email.trim() && newUser.password));

    const handleConfirm = async () => {
        if (!vacancyId) return;
        try {
            setSaving(true);
            setError(null);
            await jobVacancyService.hire(vacancyId, {
                application_id: Number(applicationId),
                ...(mode === 'existing'
                    ? { user_id: Number(userId) }
                    : { new_user: { ...newUser, role: UserRole.COLABORADOR } }),
                start_date: startDate,
                end_date: endDate || null,
                allocation_percentage: Number(percentage),
            });
            onHired(mode === 'new'
                ? `Contratação registrada. Repasse a senha temporária a ${newUser.name}: a troca é obrigatória no primeiro acesso.`
                : 'Contratação registrada: a pessoa já está alocada na posição do projeto.');
        } catch (err: any) {
            setError(err?.response?.data?.error || 'Erro ao registrar a contratação.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Fechar vaga com contratação{vacancyLabel ? ` — ${vacancyLabel}` : ''}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Informe quem foi contratado. A pessoa entra alocada na posição do projeto e passa a lançar horas nele.
                </Typography>
                {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
                {options && options.applications.length === 0 && (
                    <Alert severity="info" sx={{ my: 1 }}>Esta vaga ainda não tem candidaturas.</Alert>
                )}
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12}>
                        <TextField select fullWidth label="Candidatura contratada" value={applicationId}
                            onChange={(e) => selectApplication(e.target.value)}>
                            {(options?.applications || []).map((a) => (
                                <MenuItem key={a.id} value={String(a.id)}>{a.candidate_name} · {a.candidate_email}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value as 'existing' | 'new')}>
                            <FormControlLabel value="existing" control={<Radio />} label="Usuário existente" />
                            <FormControlLabel value="new" control={<Radio />} label="Criar usuário" disabled={!canCreateUser} />
                        </RadioGroup>
                        {!canCreateUser && (
                            <Typography variant="caption" color="text.secondary">Somente RH ou Diretor cria usuários.</Typography>
                        )}
                    </Grid>
                    {mode === 'existing' ? (
                        <Grid item xs={12}>
                            <TextField select fullWidth label="Pessoa" value={userId} onChange={(e) => setUserId(e.target.value)}>
                                {(options?.users || []).map((u) => (
                                    <MenuItem key={u.id} value={String(u.id)}>{u.name} · {u.email}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    ) : (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Nome" value={newUser.name}
                                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="E-mail (login)" value={newUser.email}
                                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Senha temporária" value={newUser.password}
                                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                    helperText="Perfil Colaborador. Troca de senha obrigatória no primeiro acesso." />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth type="date" label="Início da alocação" InputLabelProps={{ shrink: true }}
                            value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth type="date" label="Término (opcional)" InputLabelProps={{ shrink: true }}
                            value={endDate} onChange={(e) => setEndDate(e.target.value)}
                            error={datesInvalid} helperText={datesInvalid ? 'Término antes do início' : ''} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth type="number" label="% de alocação" inputProps={{ min: 1, max: 100 }}
                            value={percentage} onChange={(e) => setPercentage(e.target.value)} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={handleConfirm} disabled={saving || !ready}>Confirmar contratação</Button>
            </DialogActions>
        </Dialog>
    );
};

export default HireDialog;
